// Prediction-market exchanges (Kalshi, Polymarket, ...).
//
// An exchange is not a bookmaker. Its headline price is the RAW top of an order
// book and you pay a TAKER FEE on top, so the number a book advertises and the
// number an exchange advertises are not comparable until the fee comes off.
// Measured on live NFL head-to-head 2026-09-22: an exchange held the best price
// on 84% of selections raw, but only 38% once the fee was applied - so on
// nearly half of all best-odds cells the raw price pointed at the wrong book.
// Everything the odds page shows for an exchange is therefore fee-adjusted;
// see adjustPrice() below for exactly where that happens.
//
// ADDING AN EXCHANGE: add an entry here and to the EXCHANGE group in
// config/bookmakers.js (and to BOOKMAKERS in supabase/functions/odds-proxy,
// or the API is never asked for it). Nothing else needs to change - every
// call site reads this file.

// Taker fee per contract, as a fraction of the $1 contract:
//
//   fee = rate x p x (1 - p)        p = implied probability = 1 / decimal odds
//
// The quadratic peaks at p = 0.50 (a coin flip is the most expensive trade on
// an exchange) and collapses toward either extreme. Rates VERIFIED 2026-09-22
// against primary sources - re-check before changing:
//
//   Kalshi      0.07   Kalshi API, series KXNFLGAME: fee_multiplier = 1,
//                      fee_type = quadratic_with_maker_fees. The published
//                      formula is round up(M x 0.07 x C x P x (1-P)) and NFL
//                      carries no series override, so M = 1 here. Note Kalshi
//                      rounds UP per ORDER, so a small taker pays slightly
//                      more than this continuous rate implies.
//   Polymarket  0.05   docs.polymarket.com/trading/fees - sports category.
//                      Raised from 0.03 in July 2026. Makers pay 0 and share a
//                      15% rebate pool. This is Polymarket GLOBAL, which is
//                      what the scraper reads; Polymarket US is a separate
//                      entity on a unified 0.0695 and does NOT apply here.
//
// MAKER fills are cheaper (Kalshi 0.0175) or free with a rebate (Polymarket),
// so a resting limit order beats these numbers. The taker rate is the
// conservative assumption: it is what you pay to take the price on screen,
// which is the price being displayed.
export const EXCHANGE_TAKER_FEES = {
  Kalshi: 0.07,
  Polymarket: 0.05,
}

export const isExchange = (bookmaker) =>
  Object.prototype.hasOwnProperty.call(EXCHANGE_TAKER_FEES, bookmaker)

/**
 * Decimal odds for `bookmaker` after its taker fee, or the price unchanged for
 * an ordinary bookmaker.
 *
 * Derivation: at decimal odds d you are buying a $1 contract for p = 1/d and
 * paying rate*p*(1-p) on top, so the real cost is p + rate*p*(1-p) per $1
 * returned, and the effective odds are the reciprocal of that cost.
 *
 * Market-type agnostic ON PURPOSE. The fee is a property of the contract, not
 * of the market: a head-to-head, a spread, a total and a player prop on the
 * same exchange are all $1 binary contracts priced the same way, so one
 * function covers every market the exchanges quote, including any added later.
 *
 * Returns the input untouched for a non-exchange, a non-finite price, or a
 * price <= 1 (no payout to take a fee from), so it is always safe to call.
 */
export const adjustPrice = (price, bookmaker) => {
  const rate = EXCHANGE_TAKER_FEES[bookmaker]
  if (rate === undefined) return price
  const d = Number(price)
  if (!Number.isFinite(d) || d <= 1) return price
  const p = 1 / d
  return 1 / (p + rate * p * (1 - p))
}

// Copy for the info bubble on the EXCHANGE group in the odds-page filters.
// Deliberately SHORT: it lives inside a scrolling dropdown, so it has to be
// read at a glance, not studied. The long version of this belongs on the
// education page, not in a filter panel.
export const EXCHANGE_FEE_NOTE = {
  title: 'Prices include fees',
  body: 'Exchanges charge a fee per trade, so we subtract it before showing '
    + 'the price. Their own site shows a slightly bigger number.',
  // Rendered as small chips: "Kalshi 7%", "Polymarket 5%".
  rates: Object.entries(EXCHANGE_TAKER_FEES).map(([name, rate]) => ({
    name,
    label: `${(rate * 100).toFixed(0)}%`,
  })),
}
